import { constructMediaUrl } from '../../api/helpers';
import { getMediaFileUrl } from '../../api/mediaUrls';
import { MediaItem } from '../../api/structs/media';

/**
 * Resolves a clip's music and narrator tracks the way the render path does
 * (`pkg/remotionrender/sendMetadata.go`: `resolveMusicPayload` /
 * `resolveNarratorPayload`), so the lanes show — and the `<Player>` plays —
 * exactly the tracks a render would pick up.
 *
 * The renderer hands Remotion an uploaded file path; the browser has no clip
 * directory, so the same resolution ends in a URL instead.
 */

export interface AudioTrack {
  /** Empty when the track was resolved from a bare URL rather than a media id. */
  mediaId: string;
  url: string;
  name: string;
  /**
   * Which branch resolved it: `bound` = the clip names this media id, `url` = a
   * stored URL alias, `auto` = the render path's auto-wire of the first ready
   * audio item on the clip. Only a `bound` track can be unbound, so the lane
   * has to be able to tell them apart.
   */
  source: 'bound' | 'url' | 'auto';
}

/** A generated item sits on a sentinel URL until its file lands. */
export const isReadyMediaUrl = (fileUrl: unknown): boolean => {
  const trimmed = String(fileUrl ?? '').trim();
  return trimmed !== '' && !trimmed.includes('_waiting') && !trimmed.includes('_failed');
};

export const isFailedMediaUrl = (fileUrl: unknown): boolean =>
  String(fileUrl ?? '').includes('_failed');

const firstNonEmpty = (metadata: Record<string, any>, ...keys: string[]): string => {
  for (const key of keys) {
    const value = metadata?.[key];
    if (value === undefined || value === null) continue;
    const candidate = String(value).trim();
    if (candidate !== '') return candidate;
  }
  return '';
};

/** Filename without its extension, the most useful label a track carries. */
export const audioLabel = (item: MediaItem): string => {
  const base = String(item.file_url || '').split('/').pop() || '';
  const name = base.replace(/\.[a-z0-9]+$/i, '');
  return name || String(item.prompt || '').slice(0, 40) || 'Audio track';
};

const trackFromItem = (item: MediaItem, source: AudioTrack['source']): AudioTrack => ({
  mediaId: item.id,
  // Cache-busted: re-generating narration rewrites the item in place, and the
  // `<audio src>` would otherwise keep playing the take before it.
  url: getMediaFileUrl(item),
  name: audioLabel(item),
  source,
});

const readyItemById = (audios: MediaItem[], mediaId: string): MediaItem | null => {
  const item = audios.find((candidate) => candidate.id === mediaId);
  return item && isReadyMediaUrl(item.file_url) ? item : null;
};

const MUSIC_ID_KEYS = ['music_media_id', 'music_media_item_id', 'musicMediaId', 'musicMediaItemId'];
const NARRATOR_ID_KEYS = ['narrator_media_id', 'narratorMediaId'];

/**
 * May this unbound audio item fill the music lane? Mirrors the backend's
 * `models.IsMusicCandidate`: an item that says it is music, or one that says
 * nothing at all — pipeline music has always been created without a role, and
 * demanding one would mute every clip already in the database — and never the
 * take the clip binds as its narration.
 *
 * Auto-wiring the first ready audio item of ANY kind is what put a leftover
 * narration take on the music lane: two overlapping voices in the preview, and
 * a removed music track that came straight back because clearing the binding
 * handed the lane to the next audio item. The narrator binding is part of the
 * rule because a take created before roles were stamped carries none, yet the
 * clip names it as its narration — one lane must never claim the other's track.
 */
export const isMusicCandidate = (item: MediaItem, narratorBoundId: string): boolean => {
  if (narratorBoundId && item.id === narratorBoundId) return false;
  const role = String(item.metadata?.role ?? '')
    .trim()
    .toLowerCase();
  return role === '' || role === 'music';
};

/**
 * `music_media_id` (and its aliases) first, then a stored URL, then the
 * backend's auto-wire of the first ready audio item that could be music.
 */
export const resolveMusicTrack = (
  clipMetadata: Record<string, any>,
  audios: MediaItem[]
): AudioTrack | null => {
  const mediaId = firstNonEmpty(clipMetadata, ...MUSIC_ID_KEYS);
  if (mediaId) {
    const item = readyItemById(audios, mediaId);
    if (item) return trackFromItem(item, 'bound');
  }

  const urlFallback = firstNonEmpty(clipMetadata, 'music_media_url', 'music_url', 'musicUrl');
  if (urlFallback) {
    const matched = audios.find((item) => String(item.file_url).trim() === urlFallback);
    return matched
      ? trackFromItem(matched, 'url')
      : {
          mediaId: '',
          url: constructMediaUrl(urlFallback),
          name: audioLabel({ file_url: urlFallback } as MediaItem),
          source: 'url',
        };
  }

  if (mediaId) return null; // Bound, but the file is not there (yet).

  const narratorBoundId = firstNonEmpty(clipMetadata, ...NARRATOR_ID_KEYS);
  const auto = audios.find(
    (item) => isMusicCandidate(item, narratorBoundId) && isReadyMediaUrl(item.file_url)
  );
  return auto ? trackFromItem(auto, 'auto') : null;
};

/** `narrator_media_id`, else the first ready audio item tagged `role: narrator`. */
export const resolveNarratorTrack = (
  clipMetadata: Record<string, any>,
  audios: MediaItem[]
): AudioTrack | null => {
  const mediaId = firstNonEmpty(clipMetadata, ...NARRATOR_ID_KEYS);
  if (mediaId) {
    const item = readyItemById(audios, mediaId);
    return item ? trackFromItem(item, 'bound') : null;
  }

  const narrator = audios.find(
    (item) => item.metadata?.role === 'narrator' && isReadyMediaUrl(item.file_url)
  );
  return narrator ? trackFromItem(narrator, 'auto') : null;
};

/** The item a lane is waiting on: bound by id, but still on a sentinel URL. */
export const boundPendingAudio = (
  clipMetadata: Record<string, any>,
  audios: MediaItem[],
  key: 'music' | 'narrator'
): MediaItem | null => {
  const mediaId =
    key === 'music'
      ? firstNonEmpty(clipMetadata, ...MUSIC_ID_KEYS)
      : firstNonEmpty(clipMetadata, ...NARRATOR_ID_KEYS);
  if (!mediaId) return null;
  const item = audios.find((candidate) => candidate.id === mediaId);
  return item && !isReadyMediaUrl(item.file_url) ? item : null;
};

export type NativeAudioOverride = 'inherit' | 'on' | 'off';

/** Tri-state read of a video slide's `native_audio` override. */
export const nativeAudioOverride = (metadata: Record<string, any>): NativeAudioOverride => {
  const raw = metadata?.native_audio ?? metadata?.nativeAudio;
  if (raw === undefined || raw === null || raw === '') return 'inherit';
  const normalized = String(raw).trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on'
    ? 'on'
    : 'off';
};

/** The next state of the inherit → on → off → inherit cycle. */
export const nextNativeAudioOverride = (current: NativeAudioOverride): NativeAudioOverride =>
  current === 'inherit' ? 'on' : current === 'on' ? 'off' : 'inherit';

/** Metadata value for an override — `undefined` deletes the key (back to inherit). */
export const nativeAudioMetadataValue = (override: NativeAudioOverride): boolean | undefined =>
  override === 'inherit' ? undefined : override === 'on';
